// components/customers/QuickCreateCustomer.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { Modal, Button, Select, Spinner, Alert } from "flowbite-react";
import { useForm } from "react-hook-form";
import Input from "../inputs/Input";
import CPFInput from "../inputs/CPFInput";
import CNPJInput from "../inputs/CNPJInput";
import PhoneInput from "../inputs/PhoneInput";
import CurrencyInput from "../inputs/CurrencyInput";
import useCEP from "../../hooks/useCEP";
import { LEADS } from "../../config/apiConfig";
import axios from "axios";
import axiosInstance from "../../config/axiosInstance";

// helpers
const onlyDigits = (v = "") => String(v).replace(/\D/g, "");
const formatCEP = (v = "") => {
  const d = onlyDigits(v).slice(0, 8);
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};
const formatPhone = (ddd, numero) => {
  const d = onlyDigits(`${ddd}${numero}`).slice(0, 11);
  if (d.length === 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 11)
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return d;
};

const BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

function parseCurrencyNumber(value) {
  if (!value && value !== 0) return null;
  const str = String(value).trim();
  if (!str) return null;

  const cleaned = str.replace(/[^0-9.,-]/g, "");
  const unsigned = cleaned.replace(/[-]/g, "");
  if (!unsigned) return null;

  const isNegative = cleaned.trim().startsWith("-");
  const hasComma = unsigned.includes(",");
  const hasDot = unsigned.includes(".");
  const lastComma = unsigned.lastIndexOf(",");
  const lastDot = unsigned.lastIndexOf(".");

  let normalized = unsigned;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      normalized =
        unsigned.slice(0, lastComma).replace(/[.]/g, "") +
        "." +
        unsigned.slice(lastComma + 1);
    } else {
      normalized =
        unsigned.slice(0, lastDot).replace(/[,]/g, "") +
        "." +
        unsigned.slice(lastDot + 1);
    }
  } else if (hasComma) {
    normalized =
      unsigned.slice(0, lastComma).replace(/[.]/g, "") +
      "." +
      unsigned.slice(lastComma + 1);
  } else if (hasDot) {
    const decimals = unsigned.length - lastDot - 1;
    if (decimals > 0 && decimals <= 2) {
      normalized =
        unsigned.slice(0, lastDot).replace(/[,]/g, "") +
        "." +
        unsigned.slice(lastDot + 1);
    } else {
      normalized = unsigned.replace(/[.]/g, "");
    }
  }

  const signedValue = `${isNegative ? "-" : ""}${normalized}`;
  const number = Number(signedValue);
  return Number.isFinite(number) ? number : null;
}

export default function QuickCreateCustomer({
  show,
  onClose,
  onCreated,
  onUpdated,
  mode = "create",
  initialCompany = null,
}) {
  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      typeCustomer: "CNPJ",
      document: "",
      email: "",
      phoneNumber: "",
      name: "",
      responsibleName: "",
      responsibleCPF: "",
      position: "",
      cep: "",
      address: "",
      addressNumber: "",
      addressComplement: "",
      addressNeighborhood: "",
      addressCity: "",
      incomeMonth: "",
      presumedRevenue: "",
      state: "",
    },
  });

  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [errorCNPJ, setErrorCNPJ] = useState("");
  const [submitError, setSubmitError] = useState(""); // <- NOVO: erro de submissão
  const errorBoxRef = useRef(null); // <- para focar/rolar até a mensagem

  const typeCustomer = watch("typeCustomer");
  const cnpjValue = watch("document");
  const cepValue = watch("cep") || "";

  const {
    setCep,
    address: addressByCEP,
    loading: loadingCEP,
    error: errorCEP,
  } = useCEP();

  const onBlurSearchCEP = () => {
    if (cepValue && /\d{5}-?\d{3}/.test(cepValue)) setCep(cepValue);
  };

  // preencher quando abrir em modo edição (Lead)
  useEffect(() => {
    if (show && mode === "edit" && initialCompany) {
      reset({
        typeCustomer: initialCompany?.companyName ? "CNPJ" : "CPF",
        document: initialCompany.document || "",
        email: initialCompany.email || "",
        phoneNumber: initialCompany.phoneNumber || "",
        name: initialCompany.name || initialCompany.companyName || "",
        responsibleName: "",
        responsibleCPF: "",
        position: "",
        cep: "",
        address: "",
        addressNumber: "",
        addressComplement: "",
        addressNeighborhood: "",
        addressCity: "",
        incomeMonth: "",
        presumedRevenue:
          initialCompany.presumedRevenue != null
            ? initialCompany.presumedRevenue
            : "",
        state: initialCompany.state || "",
      });
    }
  }, [show, mode, initialCompany, reset]);

  // preenchimento via CEP (não sobrescreve se usuário já digitou)
  useEffect(() => {
    if (addressByCEP) {
      if (!watch("address")) setValue("address", addressByCEP.logradouro || "");
      if (!watch("addressNeighborhood"))
        setValue("addressNeighborhood", addressByCEP.bairro || "");
      if (!watch("addressCity"))
        setValue("addressCity", addressByCEP.localidade || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressByCEP, setValue]);

  // limpa form ao fechar (somente create)
  useEffect(() => {
    if (!show && mode === "create") reset();
  }, [show, reset, mode]);

  // auto-fetch de CNPJ
  useEffect(() => {
    const tryFetch = async () => {
      setErrorCNPJ("");
      if (watch("typeCustomer") !== "CNPJ") return;

      const digits = onlyDigits(cnpjValue);
      if (digits.length !== 14) return;

      setLoadingCNPJ(true);
      try {
        const { data } = await axios.get(`https://api.opencnpj.org/${digits}`);

        const tel0 =
          (Array.isArray(data?.telefones) && data.telefones[0]) || {};
        const socios = Array.isArray(data?.QSA) ? data.QSA : [];
        const socioAdmin =
          socios.find((s) =>
            String(s?.qualificacao_socio || "")
              .toUpperCase()
              .includes("ADMINISTRADOR")
          ) ||
          socios[0] ||
          null;

        setValue("name", data?.razao_social || "");
        setValue("email", String(data?.email || "").toLowerCase());
        setValue("phoneNumber", formatPhone(tel0?.ddd, tel0?.numero));

        const cepFmt = formatCEP(data?.cep || "");
        setValue("cep", cepFmt);
        setValue("address", data?.logradouro || "");
        setValue("addressNumber", data?.numero || "");
        setValue("addressNeighborhood", data?.bairro || "");
        setValue("addressCity", data?.municipio || "");

        setValue("responsibleName", socioAdmin?.nome_socio || "");
        setValue("responsibleCPF", socioAdmin?.cnpj_cpf_socio || "");
        setValue("state", data?.uf || "");

        if (cepFmt && /\d{5}-\d{3}/.test(cepFmt)) setCep(cepFmt);
      } catch (e) {
        console.error(e);
        setErrorCNPJ(
          e?.response?.data?.message ||
            "Não foi possível buscar os dados do CNPJ. Tente novamente."
        );
      } finally {
        setLoadingCNPJ(false);
      }
    };

    const h = setTimeout(tryFetch, 450);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjValue, setValue, setCep, watch]);

  const suggestedUserName = useMemo(() => {
    if (typeCustomer === "CNPJ") {
      return (watch("responsibleName") || watch("name") || watch("email") || "")
        .toString()
        .trim();
    }
    return (watch("name") || watch("email") || "").toString().trim();
  }, [typeCustomer, watch("name"), watch("email"), watch("responsibleName")]);

  const buildNotes = (d) => {
    const lines = [];
    if (d.typeCustomer) lines.push(`Tipo: ${d.typeCustomer}`);
    if (d.document) lines.push(`Documento: ${d.document}`);
    if (d.position) lines.push(`Cargo: ${d.position}`);
    if (d.responsibleName) lines.push(`Responsável: ${d.responsibleName}`);
    if (d.responsibleCPF) lines.push(`CPF Resp.: ${d.responsibleCPF}`);
    if (d.cep) lines.push(`CEP: ${d.cep}`);
    if (d.address) {
      const addr = `${d.address}, ${d.addressNumber || ""} ${
        d.addressComplement || ""
      }`.trim();
      lines.push(`Endereço: ${addr}`);
    }
    if (d.addressNeighborhood) lines.push(`Bairro: ${d.addressNeighborhood}`);
    if (d.addressCity) lines.push(`Cidade: ${d.addressCity}`);
    if (d.incomeMonth != null && d.incomeMonth !== "")
      lines.push(`Faturamento mensal (informado): ${d.incomeMonth}`);
    if (d.presumedRevenue)
      lines.push(`Faturamento presumido: ${d.presumedRevenue}`);
    if (d.state) lines.push(`Estado: ${d.state}`);
    return lines.join("\n");
  };

  const onSubmit = async (data) => {
    setSubmitError(""); // limpar erro anterior

    const presumedRevenueNumber = parseCurrencyNumber(data.presumedRevenue);

    const leadPayload = {
      name:
        typeCustomer === "CNPJ"
          ? data.name || data.responsibleName || data.email
          : data.name || data.email,
      companyName: typeCustomer === "CNPJ" ? data.name || "" : "",
      email: data.email,
      phoneNumber: data.phoneNumber,
      document: data.document,
      source: "manual",
      isCustomer: true,
      notes: buildNotes(data),
      state: data.state || undefined,
      presumedRevenue: presumedRevenueNumber ?? undefined,
    };

    try {
      if (mode === "edit" && initialCompany?.id) {
        await axiosInstance.put(
          `${LEADS.UPDATE}/${initialCompany.id}`,
          leadPayload
        );
        onUpdated?.(initialCompany.id, leadPayload);
        onClose?.();
      } else {
        const res = await axiosInstance.post(LEADS.CREATE, leadPayload);
        const createdLead = res?.data?.data || res?.data || null;
        if (createdLead?.id) {
          onCreated?.(createdLead);
          onClose?.();
          reset();
        }
      }
    } catch (err) {
      console.error("Erro ao salvar lead:", err);

      // Mensagem amigável exibida abaixo dos inputs
      let msg =
        "Não foi possível cadastrar o cliente. Tente novamente em instantes.";
      const status = err?.response?.status;
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;

      // Caso comum: já cadastrado / conflito
      if (status === 409) {
        msg =
          apiMsg ||
          "Este cliente já está cadastrado em nossa base. Verifique os dados ou edite o cadastro existente.";
      } else if (status === 400 || status === 422) {
        msg =
          apiMsg || "Alguns dados parecem inválidos. Revise e tente novamente.";
      } else if (apiMsg) {
        msg = apiMsg;
      }

      setSubmitError(msg);

      // opcional: rolar/focar na mensagem
      requestAnimationFrame(() => {
        errorBoxRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  };

  return (
    <Modal show={show} onClose={onClose} size="5xl">
      <Modal.Header className="py-2">
        <span className="mt-1 text-base">{mode === "edit" ? "Editar Lead" : "Novo Lead"}</span>
      </Modal.Header>
      <Modal.Body>
        <form
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* ======= DADOS DO LEAD ======= */}
          <div className="w-full lg:col-span-2">
            <Select
              id="typeCustomer"
              {...register("typeCustomer")}
              className="w-full"
            >
              <option value="CPF">Pessoa Física (CPF)</option>
              <option value="CNPJ">Pessoa Jurídica (CNPJ)</option>
            </Select>
          </div>

          {typeCustomer === "CPF" ? (
            <>
              <CPFInput
                label="CPF"
                name="document"
                required={false}
                register={register}
                error={errors.document}
                className="lg:col-span-2 w-full"
              />
              <Input
                label="Nome *"
                name="name"
                required
                register={register}
                error={errors.name?.message}
                className="lg:col-span-4"
              />
            </>
          ) : (
            <>
              <div className="lg:col-span-2 w-full">
                <CNPJInput
                  label={
                    <div className="flex items-center gap-2">
                      <span>CNPJ</span>
                      {loadingCNPJ && (
                        <span className="inline-flex items-center gap-2 text-xs text-slate-600">
                          <Spinner size="xs" /> buscando dados…
                        </span>
                      )}
                    </div>
                  }
                  name="document"
                  required={false}
                  control={control}
                  error={errors.document}
                  className="w-full"
                />
                {errorCNPJ && (
                  <div className="mt-2">
                    <Alert color="failure">{errorCNPJ}</Alert>
                  </div>
                )}
              </div>

              <Input
                label="Razão Social / Nome da Empresa *"
                name="name"
                required
                register={register}
                error={errors.name?.message}
                className="lg:col-span-2 w-full"
              />

              <Input
                label="Nome do Responsável"
                name="responsibleName"
                register={register}
                error={errors.responsibleName?.message}
                className="lg:col-span-2 w-full"
              />
            </>
          )}

          <Input
            label="E-mail *"
            name="email"
            type="email"
            required
            register={register}
            error={errors.email?.message}
            className="lg:col-span-2"
          />
          <PhoneInput
            label="Telefone *"
            name="phoneNumber"
            required={true}
            control={control}
            error={errors.phoneNumber}
            className="lg:col-span-2 w-full"
          />

          <div className="lg:col-span-1">
            <Input
              label="CEP"
              name="cep"
              register={register}
              error={errors.cep?.message}
              onBlur={onBlurSearchCEP}
            />
            <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
              {loadingCEP && (
                <>
                  <Spinner size="sm" /> Buscando endereço pelo CEP...
                </>
              )}
              {!loadingCEP && errorCEP && (
                <span className="text-red-500">
                  CEP não encontrado. Verifique e tente novamente.
                </span>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <Input
              label="Endereço"
              name="address"
              register={register}
              error={errors.address?.message}
            />
          </div>

          <div className="lg:col-span-1">
            <Input
              label="Número"
              name="addressNumber"
              type="text"
              register={register}
              error={errors.addressNumber?.message}
            />
          </div>

          <div className="lg:col-span-2">
            <Input
              label="Complemento"
              name="addressComplement"
              register={register}
              error={errors.addressComplement?.message}
            />
          </div>

          <div className="md:col-span-2">
            <Input
              label="Bairro"
              name="addressNeighborhood"
              register={register}
              error={errors.addressNeighborhood?.message}
            />
          </div>

          <div className="md:col-span-2">
            <Input
              label="Cidade"
              name="addressCity"
              register={register}
              error={errors.addressCity?.message}
            />
          </div>

          <CurrencyInput
            label="Faturamento Presumido (Anual)"
            name="presumedRevenue"
            control={control}
            error={errors.presumedRevenue?.message}
            placeholder="R$ 0,00"
            className="lg:col-span-2"
          />
          <div className="lg:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Estado</label>
            <Select {...register("state")} className="w-full" defaultValue="">
              <option value="">Selecione o Estado</option>
              {BRAZILIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </div>

          {/* ======= MENSAGEM DE ERRO DA SUBMISSÃO ======= */}
          {submitError && (
            <div className="md:col-span-4" ref={errorBoxRef}>
              <Alert color="failure">{submitError}</Alert>
            </div>
          )}

          {/* ======= AÇÕES ======= */}
          <div className="md:col-span-4 mt-2 flex justify-end gap-2">
            <Button
              color="gray"
              type="button"
              onClick={() => {
                onClose?.();
                if (mode === "create") reset();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
